<template>
  <component-wrapper class="brochures-fi">
    <grid-container-fi>
      <div class="fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12">
        <richtext-fi
          v-if="fields?.headline?.value || isEditMode"
          class="brochures-fi__headline"
          :html-content="fields.headline"
        />
      </div>
    </grid-container-fi>
    <grid-container-fi :container="false">
      <div class="fi-col-span-full">
        <tab-navigation-fi
          :tab-navigation="tabNavigation"
          :no-spacing-below="true"
          @change="(value) => tabChanged(value)"
        />
      </div>
    </grid-container-fi>
    <grid-container-fi :container="false">
      <div class="brochures-fi__container fi-col-span-full">
        <grid-container-fi>
          <div
            class="brochures-fi__content-wrapper fi-col-start-1 fi-col-end-13 md:fi-col-start-2 md:fi-col-end-12"
          >
            <!-- eslint-disable vue/no-v-html -->
            <h3
              v-if="isEditMode"
              class="brochures-fi__tiles-intro-text"
              v-html="fields.tilesIntroText?.editable"
            />
            <h3
              v-else
              class="brochures-fi__tiles-intro-text"
              v-html="fields.tilesIntroText ? fields.tilesIntroText.value : ''"
            />

            <loading-indicator
              v-if="isLoading"
              variant="spinnerLocal"
              class="spinner-local--rebrush"
              dark
            />

            <!-- eslint-enable vue/no-v-html -->
            <transition
              enter-active-class="animated fadeIn fastest"
              leave-active-class="animated fadeOut fastest"
              mode="out-in"
            >
              <div
                :key="currentTag.id"
                class="brochures-fi__tiles-wrapper"
              >
                <div class="brochures-fi__brochure-tiles">
                  <brochure-tile-fi
                    v-for="(brochure, index) in brochureList"
                    :key="brochure.Document.Download + index"
                    :title="brochure.Document.BrochureTitle"
                    :preview-text="brochure.Document.PreviewText"
                    :download-url="brochure.Document.Download"
                    :thumbnail-url="brochure.Document.Thumbnail"
                    :target="brochure.Document.Target ? brochure.Document.Target : null"
                  />
                </div>
              </div>
            </transition>
          </div>
        </grid-container-fi>
      </div>
    </grid-container-fi>
  </component-wrapper>
</template>

<script setup lang="ts">
import { LoadingIndicator, RichtextFi } from 'atoms';
import { computed, inject, onMounted, onServerPrefetch, ref, useSSRContext } from 'vue';

import BrochureTileFi from '@/components/brochure-tile-fi/BrochureTileFi.vue';
import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import TabNavigationFi from '@/components/tab-navigation-fi/TabNavigationFi.vue';
import { useFields } from '@/composables/fields';
import { useFischer } from '@/composables/fischerPlugin';

defineOptions({ name: 'BrochuresFi' });

interface BrochureTab {
  id: number;
  tagId?: string;
}

const fischer = useFischer();
const fields = useFields('BrochuresFiFields');
const prefetch = inject<Record<string, any> | undefined>('prefetch', undefined);

const currentTag = ref<BrochureTab>({ id: 0 });

let cache: Record<string, any>;
let ctx: Record<string, any> = {};
if (IS_SSR) {
  cache = {};
  ctx = useSSRContext() || {};
} else {
  cache = prefetch || {};
}

const tags = computed(() => {
  if (fields?.tags?.length) {
    return fields.tags.filter(
      (tag: any) => tag.fields?.tagName?.value && tag.fields.tagName.value !== '',
    );
  }

  return [];
});

const tabNavigation = computed(() => {
  if (tags.value.length) {
    return tags.value.map((tag: any, index: number) => ({
      id: index,
      text: tag.fields?.tagName?.value,
      tagId: tag.id,
    }));
  }

  return null;
});

const fetchParams = computed(() => {
  const lang = fischer.siteLanguage() || null;
  if (!lang || !tabNavigation.value) {
    return null;
  }
  const { tagId } = tabNavigation.value[0];
  const tag = currentTag.value.tagId || tagId;

  return {
    lang,
    tags: tag,
  };
});

const cacheKey = computed(() => {
  if (!fetchParams.value) {
    return null;
  }
  return `${fetchParams.value.lang} ${fetchParams.value.tags}`;
});

// The server renders once onServerPrefetch has resolved, so its markup shows the fetched tiles and
// no spinner. The first client render is the hydration render, before onMounted, and has to match
// that from the prefetch cache alone - so both initial values belong here and not in a hook.
const initialList = !IS_SSR && cacheKey.value ? cache[cacheKey.value] : null;
const brochureList = ref<any[]>(initialList || []);
// Not `true`: the server's `finally` leaves it false by render time whether or not the fetch
// succeeded, so only an actually-started fetch may turn it on.
const isLoading = ref(false);

async function fetchData() {
  isLoading.value = true;
  try {
    const fromCache = cacheKey.value ? cache[cacheKey.value] : null;
    if (fromCache) {
      brochureList.value = fromCache;
      return;
    }
    let url = '/api/brochures/get';
    if (IS_SHOWROOM) {
      url = `/__mocks__/sitecoreApi${url}/mockedBrochuresData.json`;
    }
    if (IS_SSR) {
      url = `${fischer.hostUrlBase()}${url}`;
    }

    if (!fetchParams.value) {
      console.error('Missing parameters', fetchParams.value);
      return;
    }
    const params = new URLSearchParams(fetchParams.value);
    const response = await fetch(`${url}?${params.toString()}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch brochures: ${response.status}`);
    }
    const data = await response.json();
    if (data?.Data?.TeaserList) {
      brochureList.value = data.Data.TeaserList;
      if (cacheKey.value) {
        cache[cacheKey.value] = brochureList.value;
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
}

function tabChanged(value: BrochureTab) {
  currentTag.value = value;
  fetchData();
}

onServerPrefetch(async () => {
  await fetchData();
  ctx.prefetch = cache;
});

onMounted(() => {
  fetchData();
});
</script>

<style scoped src="./brochures-fi" />
